import { createCmlContentDefinitionValueString } from 'bundles/cml';
import type { FaqFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';

import _t from 'i18n!nls/xdp';

export const getProjectFaqs = (canEnrollWithFullDiscount = false): Array<FaqFragment> => {
  const contactUsLink = `<a href="https://www.coursera.support/s/learner-help-center-contact-us" target = "_blank">${_t(
    'contact us'
  )}</a>`;
  const unableToAccessProjectAnswer = _t(
    'In rare instances, Projects may be taken down for maintenance or other reasons. If you are experiencing any issues, please #{contactUsLink}.',
    {
      contactUsLink,
    }
  );
  const unableToAccessProjectAnswerCMLText = createCmlContentDefinitionValueString(unableToAccessProjectAnswer);

  const purchaseProject = {
    id: '1',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('What will I get if I purchase a Project?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          "By purchasing a Project, you'll get everything you need to complete the Project including temporary access to any product required to complete the Project."
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const availableDesktopMobile = {
    id: '2',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Are Projects available on desktop and mobile?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'Even though Projects are technically available on mobile devices, we highly recommend that you complete Projects on a laptop or desktop only.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const downloadProject = {
    id: '3',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Can I download the work from my Project after I complete it?')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'Yes, you can download and keep any of your created files from the Project. To do so, please make sure you save any files and work to your device before exiting the product environment.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const financialAidAvailable = {
    id: '5',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Is financial aid available?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Financial aid is not available for Projects.')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const auditWatchVideoForFree = {
    id: '6',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Can I audit a Project?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Auditing is not available for Projects.')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const unableToAccessProject = {
    id: '7',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t("What if I can't access a Project I started?")),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: unableToAccessProjectAnswerCMLText,
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const howMuchExperience = {
    id: '8',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('How much experience do I need to do this Project?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('At the top of the page, you can view the experience level recommended for this Project.')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const installSpecialSoftware = {
    id: '9',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Can I complete this Project through my web browser, instead of installing special software?')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Yes, everything you need to complete your Project will be available in your browser.')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const learningExperience = {
    id: '10',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('What is the learning experience like with Projects?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          "In Projects, you'll complete an activity or scenario by following a set of instructions in an interactive hands-on environment. Projects are completed in a real cloud environment and within real instances of various products as opposed to a simulation or demo environment."
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  if (canEnrollWithFullDiscount) {
    return [learningExperience, availableDesktopMobile, howMuchExperience, installSpecialSoftware];
  } else {
    return [
      learningExperience,
      purchaseProject,
      availableDesktopMobile,
      downloadProject,
      financialAidAvailable,
      unableToAccessProject,
      auditWatchVideoForFree,
      howMuchExperience,
      installSpecialSoftware,
    ];
  }
};
